import { Close as CloseIcon } from '@mui/icons-material'
import { Button, DialogActions, DialogContent, DialogTitle, Tab, Tabs, Typography } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { useTranslation } from 'react-i18next'
import TabPanel from '../../../../../shared/components/TabPanel'
import { CancelButton, SaveButton } from '../../../../../shared/components/inputs'
import { StyledDialog } from '../../../../../shared/styles/StyledDialog'
import { useSliderForm } from '../../hooks/useSliderForm'
import { useSliderState } from '../../hooks/useSliderState'
import ComplementsInfoForm from './ComplementsInfoForm'
import GeneralInfoForm from './GeneralInfoForm'
import SliderModalHeader from './SliderModalHeader'
import useTabState from '@/contexts/shared/hooks/useTabState'
import { IRootState } from '@/redux/types'
import { useSelector } from 'react-redux'

type SliderModalProps = {
  type: number
  position?: number | null
  title: string
}

function SliderModal({ type, position, title }: SliderModalProps) {
  const { t } = useTranslation(['global'])

  const { isValidForm, msgForm } = useSelector((state: IRootState) => state.validator)
  const { sliderState, setSliderState, handleInputChange, titleLanguages, setTitleLanguages, STATUS } = useSliderState({
    type,
    position
  })
  const { handleCloseModalSlider, handleSubmit, showForm } = useSliderForm({
    type,
    sliderState,
    titleLanguages
  })
  const { currentTab, setCurrentTab } = useTabState()

  return (
    <StyledDialog
      open={showForm}
      onClose={handleCloseModalSlider}
      aria-labelledby="dialog-slider-title"
      aria-describedby={'dialog-slider-desc'}
    >
      <DialogTitle id="dialog-slider-title">
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {sliderState.id !== 0 ? t('edit') : t('create')} {title}
          </Typography>
          <Button onClick={handleCloseModalSlider} color="primary" sx={{ minWidth: 'auto', padding: 1 }}>
            <CloseIcon />
          </Button>
        </Grid>
      </DialogTitle>
      <DialogContent sx={{ pb: 3, pt: 2 }}>
        <SliderModalHeader sliderState={sliderState} handleInputChange={handleInputChange} />
        <Grid xs={12}>
          <Tabs
            value={currentTab}
            onChange={(_, value) => setCurrentTab(value)}
            aria-label="slider form tabs"
            sx={{
              marginTop: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                borderBottom: '2px solid transparent',
                color: theme => theme.palette.grey[500],
                '&.Mui-selected': {
                  borderBottom: theme => `2px solid ${theme.palette.secondary.main}`,
                  color: 'black'
                }
              },
              '& .MuiTabs-indicator': {
                display: 'none'
              }
            }}
          >
            <Tab label={t('general')} aria-controls="tabpanel-0" />
            {type !== 3 && <Tab label={type === 2 ? `${t('add-ons')}-${t('language')}` : t('add-ons')} aria-controls="tabpanel-1" />}
          </Tabs>
        </Grid>

        <TabPanel value={currentTab} index={0}>
          <GeneralInfoForm type={type} sliderState={sliderState} setSliderState={setSliderState} STATUS={STATUS} />
        </TabPanel>

        {type !== 3 && (
          <TabPanel value={currentTab} index={1}>
            <ComplementsInfoForm
              type={type}
              sliderState={sliderState}
              handleInputChange={handleInputChange}
              titleLanguages={titleLanguages}
              setTitleLanguages={setTitleLanguages}
            />
          </TabPanel>
        )}
      </DialogContent>

      <DialogActions sx={[!isValidForm && Object.keys(msgForm).length > 0 && { bgcolor: 'warning.light' }]}>
        <CancelButton onClick={handleCloseModalSlider}>{t('cancel')}</CancelButton>
        <SaveButton id="btn-save-slider" onClick={handleSubmit}>
          {sliderState.id !== null ? t('save') : t('create')}
        </SaveButton>
      </DialogActions>
    </StyledDialog>
  )
}

export default SliderModal
